<script setup>
import AuthBrandLogo from '@/components/authentication/AuthBrandLogo.vue'

const props = defineProps({
    content: {
        type: Object,
        required: true,
    },
    metrics: {
        type: Array,
        default: () => [],
    },
    professionals: {
        type: Array,
        default: () => [],
    },
})
</script>

<template>
    <aside class="brand-panel">
        <div class="brand-panel__background" aria-hidden="true">
            <span class="brand-panel__circle brand-panel__circle--large"></span>
            <span class="brand-panel__circle brand-panel__circle--medium"></span>
            <span class="brand-panel__circle brand-panel__circle--small"></span>
            <svg class="brand-panel__pattern">
                <defs>
                    <pattern id="auth-brand-dot-grid" x="0" y="0" width="26" height="26" patternUnits="userSpaceOnUse">
                        <circle cx="2" cy="2" r="1.4" fill="#fff" />
                    </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#auth-brand-dot-grid)" />
            </svg>
            <span class="brand-panel__corner"></span>
        </div>

        <AuthBrandLogo :content="props.content" />

        <div class="brand-panel__content">
            <div class="brand-panel__badge">
                <span></span>
                {{ props.content.badge }}
            </div>

            <h1>
                {{ props.content.title }}
                <span v-if="props.content.accent">{{ props.content.accent }}</span>
            </h1>

            <p>
                {{ props.content.description }}
            </p>

            <div class="brand-panel__highlights">
                <article
                    v-for="highlight in props.content.highlights"
                    :key="highlight.title"
                    class="brand-panel__highlight"
                >
                    <span class="material-symbols-outlined">{{ highlight.icon }}</span>
                    <div>
                        <strong>{{ highlight.title }}</strong>
                        <small>{{ highlight.description }}</small>
                    </div>
                </article>
            </div>

            <div v-if="props.metrics.length" class="brand-panel__metrics">
                <div v-for="metric in props.metrics" :key="metric.label">
                    <strong>{{ metric.value }}</strong>
                    <small>{{ metric.label }}</small>
                </div>
            </div>
        </div>

        <div v-if="props.professionals.length || props.content.trustText" class="brand-panel__trust">
            <div v-if="props.professionals.length" class="brand-panel__avatars" aria-hidden="true">
                <span
                    v-for="professional in props.professionals"
                    :key="professional.initials"
                    :style="{ backgroundColor: professional.color }"
                >
                    {{ professional.initials }}
                </span>
            </div>
            <div>
                <strong v-if="props.content.trustTitle">{{ props.content.trustTitle }}</strong>
                <small v-if="props.content.trustText">{{ props.content.trustText }}</small>
            </div>
        </div>
    </aside>
</template>

<style lang="scss" scoped>
.brand-panel {
    width: 46%;
    min-width: 460px;
    position: relative;
    overflow: hidden;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 44px 52px;
    background: linear-gradient(150deg, #0f0f0f 0%, #3f3c49 40%, #000000 75%, #000000 100%);

    &__background {
        position: absolute;
        inset: 0;
        pointer-events: none;
        overflow: hidden;
    }

    &__circle {
        position: absolute;
        display: block;
        border-radius: 50%;
        --circle-move-x: 10px;
        --circle-move-y: -10px;
        animation: auth-brand-circle-move 15s ease-in-out infinite;

        &--large {
            width: 460px;
            height: 460px;
            top: -140px;
            right: -100px;
            --circle-move-x: 34px;
            --circle-move-y: -28px;
            background: rgba(143, 122, 11, 0.38);
        }

        &--medium {
            width: 320px;
            height: 320px;
            bottom: 60px;
            left: -90px;
            background: rgba(143, 122, 11, 0.18);
        }

        &--small {
            width: 160px;
            height: 160px;
            top: 42%;
            right: 60px;
            background: rgba(143, 122, 11, 0.18);
        }
    }

    &__pattern {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        opacity: 0.06;
    }

    &__corner {
        position: absolute;
        right: 0;
        bottom: 0;
        width: 200px;
        height: 200px;
        background: linear-gradient(135deg, transparent 50%, rgba(255, 255, 255, 0.05) 50%);
    }

    &__logo,
    &__content,
    &__trust {
        position: relative;
        z-index: 2;
    }

    &__content {
        flex: 1;
        display: flex;
        flex-direction: column;
        justify-content: center;
        padding-top: 24px;
    }

    &__badge {
        width: fit-content;
        display: inline-flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 22px;
        padding: 6px 14px;
        border: 1px solid rgba(255, 255, 255, 0.2);
        border-radius: 30px;
        background: rgba(255, 255, 255, 0.12);
        color: rgba(255, 255, 255, 0.9);
        font-size: 11px;
        font-weight: 600;
        line-height: 1;
        white-space: nowrap;

        span {
            width: 8px;
            height: 8px;
            flex-shrink: 0;
            display: block;
            border-radius: 50%;
            background: #17cf97;
        }
    }

    h1 {
        margin: 0 0 14px;
        color: #fff;
        font-size: 36px;
        font-weight: 800;
        line-height: 1.2;

        span {
            display: block;
            width: fit-content;
            background: linear-gradient(90deg, #c6b814, #ffbd00);
            -webkit-background-clip: text;
            background-clip: text;
            -webkit-text-fill-color: transparent;
        }
    }

    p {
        max-width: 490px;
        margin: 0 0 32px;
        color: rgba(255, 255, 255, 0.6);
        font-size: 18px;
        line-height: 1.75;
    }

    &__highlights {
        display: flex;
        flex-direction: column;
        gap: 14px;
    }

    &__highlight {
        display: flex;
        align-items: flex-start;
        gap: 14px;

        > span {
            width: 40px;
            height: 40px;
            flex-shrink: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            border: 1px solid rgba(255, 255, 255, 0.14);
            border-radius: 10px;
            background: rgba(255, 255, 255, 0.1);
            color: #fff;
            font-size: 20px;
        }

        strong,
        small {
            display: block;
        }

        strong {
            margin-bottom: 2px;
            color: #fff;
            font-size: 18px;
            font-weight: 700;
        }

        small {
            color: rgba(255, 255, 255, 0.52);
            font-size: 14px !important;
            line-height: 1.5;
        }
    }

    &__metrics {
        display: flex;
        margin-top: 36px;
        overflow: hidden;
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 12px;
        background: rgba(255, 255, 255, 0.07);

        div {
            flex: 1;
            padding: 16px 0;
            text-align: center;

            &:not(:last-child) {
                border-right: 1px solid rgba(255, 255, 255, 0.1);
            }
        }

        strong,
        small {
            display: block;
        }

        strong {
            color: #fff;
            font-size: 20px;
            font-weight: 800;
        }

        small {
            margin-top: 3px;
            color: rgba(255, 255, 255, 0.48);
            font-size: 11px;
        }
    }

    &__trust {
        display: flex;
        align-items: center;
        gap: 14px;

        strong,
        small {
            display: block;
        }

        strong {
            color: #fff;
            font-size: 13px;
            font-weight: 700;
        }

        small {
            color: rgba(255, 255, 255, 0.48);
            font-size: 11px;
        }
    }

    &__avatars {
        display: flex;

        span {
            width: 34px;
            height: 34px;
            display: flex;
            align-items: center;
            justify-content: center;
            border: 2.5px solid rgba(255, 255, 255, 0.25);
            border-radius: 50%;
            color: #fff;
            font-size: 11px;
            font-weight: 700;

            &:not(:first-child) {
                margin-left: -10px;
            }
        }
    }
}

@keyframes auth-brand-circle-move {
    0%,
    100% {
        transform: translate3d(0, 0, 0);
    }

    50% {
        transform: translate3d(var(--circle-move-x), var(--circle-move-y), 0);
    }
}

@media only screen and (max-width: 1180px) {
    .brand-panel {
        width: 42%;
        min-width: 390px;
        padding: 36px 34px;

        h1 {
            font-size: 31px;
        }
    }
}

@media only screen and (max-width: 960px) {
    .brand-panel {
        width: 100%;
        min-width: 0;
        min-height: auto;
        padding: 30px 24px;

        &__content {
            padding-top: 44px;
        }

        &__trust {
            margin-top: 34px;
        }
    }
}

@media only screen and (max-width: 575px) {
    .brand-panel {
        padding: 24px 18px;

        h1 {
            font-size: 28px;
        }

        p {
            margin-bottom: 24px;
        }

        &__metrics {
            flex-direction: column;

            div:not(:last-child) {
                border-right: 0;
                border-bottom: 1px solid rgba(255, 255, 255, 0.1);
            }
        }

        &__trust {
            align-items: flex-start;
            flex-direction: column;
        }
    }
}
</style>
